import React, { useMemo, useState } from "react";

import {
  applySubstitutions,
  CADENCES,
  CadenceTouch,
  pendingFieldCount,
  suggestedCadenceKey,
} from "../lib/cadences";
import { Opportunity } from "../types/crm";

interface FollowupEmailModalProps {
  opportunity: Opportunity;
  onClose: () => void;
  onReschedule?: (dueDate: string) => Promise<void> | void;
}

interface TouchOption {
  value: string; // "cadenceKey:n" ou "blank"
  touch: CadenceTouch | null;
  cadenceNome: string;
}

/**
 * Modal "Preparar e-mail de follow-up" (spec §6.5). O responsável ESCOLHE o
 * toque da cadência; os campos são preenchidos a partir do modelo e revisados
 * antes de abrir no Outlook (mailto). Nada é enviado automaticamente.
 */
export function FollowupEmailModal({ opportunity, onClose, onReschedule }: FollowupEmailModalProps) {
  const vars = {
    empresa: opportunity.company_detail.name,
    nome: opportunity.primary_contact_detail?.name,
    cargo: opportunity.primary_contact_detail?.role,
  };
  const suggested = suggestedCadenceKey(opportunity.stage_detail.name);

  const [selected, setSelected] = useState<string>("");
  const [para, setPara] = useState(opportunity.primary_contact_detail?.email ?? "");
  const [assunto, setAssunto] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [nextFup, setNextFup] = useState("");
  const [copied, setCopied] = useState(false);

  const options = useMemo<TouchOption[]>(() => {
    const opts: TouchOption[] = [];
    for (const cadence of CADENCES) {
      for (const touch of cadence.touches) {
        opts.push({ value: `${cadence.key}:${touch.n}`, touch, cadenceNome: cadence.nome });
      }
    }
    opts.push({ value: "blank", touch: null, cadenceNome: "Outros" });
    return opts;
  }, []);

  const currentTouch = options.find((o) => o.value === selected)?.touch ?? null;

  const applyTouch = (value: string) => {
    setSelected(value);
    setCopied(false);
    if (value === "blank" || !value) {
      setAssunto("");
      setMensagem("");
      return;
    }
    const opt = options.find((o) => o.value === value);
    if (!opt || !opt.touch) return;
    setAssunto(applySubstitutions(opt.touch.assunto, vars));
    const body = applySubstitutions(opt.touch.copy, vars) + "\n\nAbraço,\n[seu nome]";
    setMensagem(body);
  };

  const fieldsPending = pendingFieldCount(assunto) + pendingFieldCount(mensagem);
  const isWhatsapp = currentTouch?.canal === "WhatsApp";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${assunto}\n\n${mensagem}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard pode falhar em http; ignora */
    }
  };

  const handleOutlook = async () => {
    const href = `mailto:${encodeURIComponent(para)}?subject=${encodeURIComponent(
      assunto
    )}&body=${encodeURIComponent(mensagem)}`;
    window.open(href, "_blank");
    if (nextFup && onReschedule) await onReschedule(nextFup);
  };

  const cadenceLabel = (cadenceKey: "nutricao" | "acompanhamento") =>
    CADENCES.find((c) => c.key === cadenceKey)!.nome;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 py-12">
      <div className="max-h-[90vh] w-full max-w-[620px] overflow-y-auto rounded-xl border border-border-subtle bg-white p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Preparar e-mail de follow-up</h2>
            <p className="mt-0.5 flex items-center gap-2 text-sm text-slate-500">
              {opportunity.company_detail.name}
              <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary">
                {opportunity.stage_detail.name}
              </span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            ✕
          </button>
        </div>

        {/* Seletor de toque da cadência */}
        <div className="mt-4 rounded-[10px] bg-surface p-3">
          <label htmlFor="touch" className="block text-sm font-medium text-slate-700">
            Escolha o toque da cadência
          </label>
          <select
            id="touch"
            value={selected}
            onChange={(e) => applyTouch(e.target.value)}
            className="mt-1 block w-full rounded-[10px] border border-border-subtle bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Selecione um modelo…</option>
            {CADENCES.map((cadence) => (
              <optgroup key={cadence.key} label={`${cadence.nome} · ${cadence.contexto}`}>
                {cadence.touches.map((touch) => (
                  <option key={touch.n} value={`${cadence.key}:${touch.n}`}>
                    {touch.n}. {touch.dia} · {touch.canal} — {touch.objetivo}
                  </option>
                ))}
              </optgroup>
            ))}
            <optgroup label="Outros">
              <option value="blank">E-mail em branco</option>
            </optgroup>
          </select>

          {!selected && suggested && (
            <p className="mt-2 text-xs text-slate-500">
              Pela etapa atual, a cadência de <strong>{cadenceLabel(suggested)}</strong> costuma ser
              a indicada.
            </p>
          )}
          {currentTouch && (
            <span className="mt-2 inline-block rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary">
              Objetivo: {currentTouch.objetivo}
            </span>
          )}
        </div>

        {isWhatsapp && (
          <div className="mt-3 rounded-[10px] bg-accent-soft px-3 py-2 text-sm text-accent-dark">
            Este toque da cadência é por WhatsApp. Copie o texto abaixo e envie pelo seu aparelho — o
            botão do Outlook não se aplica.
          </div>
        )}

        <div className="mt-3 rounded-[10px] bg-[#FFFBEB] px-3 py-2 text-sm text-[#92400E]">
          Preencha os campos [entre colchetes] e ajuste o tom antes de enviar. Nada é enviado
          automaticamente.
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="para" className="block text-sm font-medium text-slate-700">
              Para
            </label>
            <input
              id="para"
              type="email"
              value={para}
              onChange={(e) => setPara(e.target.value)}
              className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="assunto" className="block text-sm font-medium text-slate-700">
              Assunto
            </label>
            <input
              id="assunto"
              type="text"
              value={assunto}
              onChange={(e) => setAssunto(e.target.value)}
              className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="mensagem" className="block text-sm font-medium text-slate-700">
                Mensagem
              </label>
              <span
                className={`text-xs ${fieldsPending > 0 ? "text-[#92400E]" : "text-accent-dark"}`}
              >
                {fieldsPending > 0
                  ? `${fieldsPending} campos a preencher`
                  : "Nenhum campo pendente"}
              </span>
            </div>
            <textarea
              id="mensagem"
              rows={9}
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="nextfup" className="block text-sm font-medium text-slate-700">
              Reagendar próximo follow-up (opcional)
            </label>
            <input
              id="nextfup"
              type="date"
              value={nextFup}
              onChange={(e) => setNextFup(e.target.value)}
              className="mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="rounded-[10px] border border-border-subtle px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface"
          >
            {copied ? "Copiado ✓" : "Copiar texto"}
          </button>
          <button
            type="button"
            onClick={handleOutlook}
            className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
          >
            Abrir no Outlook
          </button>
        </div>
      </div>
    </div>
  );
}
